import {
  isAssetReadOnly,
  PermissionValues,
  useCurrentAccount,
  useIsGuestOfAssetWorkspace,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import type { ColumnData, SlideLayoutLocationState } from '@gripp/shared-ui';
import {
  EntitySearchInput,
  FilterHeaders,
  ImageGalleryImage,
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { AddFilesLinksHeaderButton } from './addFilesLinksButton';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';
import {
  FilesContext,
  FilesContextType,
  MediaExtended,
  ThirdPanelMode,
} from './filesContext';
import { FilterPanel, FilterPanelHeaderActions } from './filesFilterPanel';
import { FileRightColumn } from './filesRightColumn';

export const AssetFiles = () => {
  const { assetId, issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const { workspaceAsset, isLoading: isWorkspaceAssetLoading } =
    useWorkspaceAsset(assetId ?? '');
  const { account } = useCurrentAccount();
  const assetWorkspaceId = workspaceAsset?.workspace?.id;
  // On the guest allowlist: this route lists every file on the asset, and a
  // Guest's files fold is a read-only view of the same data. The shared hook
  // fails closed while account/roles are unresolved, so a cold load renders
  // the read-only shape first rather than flashing the edit controls.
  const isWorkspaceAssetResolved = !isWorkspaceAssetLoading && !!assetWorkspaceId;
  const isGuestOfAssetWorkspace = useIsGuestOfAssetWorkspace(assetWorkspaceId);
  // `isAssetReadOnly`/`hasPermission` key off the caller's AMBIENT role
  // (whatever workspace they are currently switched into), not this asset's
  // workspace, so they read as editable for a guest browsing from My
  // History (`General`) or their own personal workspace (`Workspace
  // Admin`) -- both hold `asset.update`. The guest determination has to be
  // applied explicitly here, not left to isAssetReadOnly alone.
  const canEdit =
    account?.hasPermission(PermissionValues.asset.update) &&
    !isGuestOfAssetWorkspace;
  const isReadOnlyMode =
    isAssetReadOnly(account, workspaceAsset) || isGuestOfAssetWorkspace;
  const [searchKeyword, setSearchKeyword] = useState('');
  const [galleryImages, setGalleryImages] = useState<ImageGalleryImage[]>([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [thirdPanelMode, setThirdPanelMode] =
    useState<ThirdPanelMode>('gallery');
  const [allFiles, setAllFiles] = useState<MediaExtended[]>([]);
  const [filteredCount, setFilteredCount] = useState<number>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);
  const [isThirdPanelOpen, setIsThirdPanelOpen] = useState(false);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);

  // Open the add sheet when navigated here with that intent (e.g. from the
  // "+ Add file" button on the AssetCard files fold). Consume the flag so a
  // later back-nav to this page doesn't auto-reopen the sheet.
  useEffect(() => {
    if (location.state?.openAddSheet) {
      setIsAddSheetOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  if (!assetId) {
    return null;
  }

  // Wait rather than decide: account, roles and the asset's own workspace
  // are all unresolved on a cold load or refresh, and isGuestOfAssetWorkspace
  // below is only meaningful once they are.
  if (!isWorkspaceAssetResolved) {
    return <LoadingIndicator flexContainer />;
  }

  const handleSearchClose = () => {
    setSearchKeyword('');
  };

  const handleSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: issueId
            ? `/activities/${issueId}/asset/${assetId}`
            : `/assets/${assetId}`,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: (
      <AssetFilters
        assetId={assetId}
        showEditButton={!isGuestOfAssetWorkspace}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(issueId ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: false,
  };

  const contextValue: FilesContextType = {
    galleryImages,
    setGalleryImages,
    galleryInitialIndex,
    setGalleryInitialIndex,
    thirdPanelMode,
    setThirdPanelMode,
    allFiles,
    selectedCategoryId,
    setSelectedCategoryId,
    isThirdPanelOpen,
    setIsThirdPanelOpen,
    isAddSheetOpen,
    setIsAddSheetOpen,
  };

  const rightColumn: ColumnData = {
    title: 'asset.files.title',
    count: filteredCount,
    mainComponent: (
      <FilesContext.Provider value={contextValue}>
        <FileRightColumn
          assetId={assetId}
          issueId={issueId}
          searchKeyword={searchKeyword}
          selectedCategoryId={selectedCategoryId}
          onAllFilesLoaded={setAllFiles}
          onFilteredCountChange={setFilteredCount}
        />
      </FilesContext.Provider>
    ),
    headerComponent: (
      <FilesContext.Provider value={contextValue}>
        <AssetFilesHeader
          onSearchClose={handleSearchClose}
          onFilterPress={() => {
            setThirdPanelMode('filter');
            setIsThirdPanelOpen(true);
          }}
          searchKeyword={searchKeyword}
          onSearch={handleSearch}
          showAddButton={!!(workspaceAsset?.asset && !isReadOnlyMode)}
          canEdit={!!canEdit}
        />
      </FilesContext.Provider>
    ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => {
      navigate('../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
  };

  const thirdColumnDef = isThirdPanelOpen
    ? {
        title: 'common.filter',
        count: filteredCount,
        mainComponent: (
          <FilesContext.Provider value={contextValue}>
            <FilterPanel />
          </FilesContext.Provider>
        ),
        headerComponent: (
          <FilterPanelHeaderActions
            onReset={() => setSelectedCategoryId(undefined)}
          />
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsThirdPanelOpen(false),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumnDef}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

export type AssetFilesHeaderProps = {
  onSearchClose: () => void;
  onFilterPress: () => void;
  searchKeyword: string;
  onSearch: (keyword: string) => Promise<void>;
  showAddButton: boolean;
  canEdit: boolean;
};

export const AssetFilesHeader = ({
  onSearchClose,
  onFilterPress,
  searchKeyword,
  onSearch,
  showAddButton,
  canEdit,
}: AssetFilesHeaderProps) => (
  <View style={styles.headerActions}>
    <FilterHeaders
      onSearchClose={onSearchClose}
      onFilterPress={onFilterPress}
      currentSearchKeyword={searchKeyword}
      searchComponent={
        <EntitySearchInput
          filter={{ keyword: searchKeyword }}
          onSearch={onSearch}
          placeholder="Search files..."
        />
      }
      Routes={{}}
    />
    {showAddButton && <AddFilesLinksHeaderButton disabled={!canEdit} />}
  </View>
);

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
